import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

export default function Origins() {
  const ref = useRef(null);
  useRevealAll(ref);

  return (
    <section
      ref={ref}
      data-testid="origins-section"
      className="bg-[color:var(--he-cream)] py-28 md:py-44 relative overflow-hidden"
    >
      <div className="he-container">
        {/* heading */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 md:mb-28">
          <div className="md:col-span-4 reveal">
            <p className="overline mb-6 mag-marker">
              <span>Két forrás</span>
            </p>
            <span className="he-divider" />
          </div>
          <div className="md:col-span-8 reveal delay-1">
            <h2 className="font-serif-display text-[2.4rem] sm:text-[3rem] md:text-[4.6rem] leading-[1.02] tracking-[-0.018em]">
              Két származás. <span className="italic text-[color:var(--he-gold)]">Ugyanaz a mérce.</span>
            </h2>
          </div>
        </div>

        {/* Split cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-2">
          <OriginCard
            title="Európai"
            subtitle="Vékony szálú, lágy struktúra, tökéletes illeszkedés"
            number="EU"
            number2="01"
            text="Vékonyabb szálszerkezet, lágy textúra. Akkor válaszd, ha a saját hajad is finomszálú, vagy ha visszafogott, természetes hatást szeretnél."
            image="/brand/eu-origin.jpg"
            link="/termekek?o=europai"
            testid="origin-eu"
          />
          <OriginCard
            title="Kazahsztáni"
            subtitle="Közép szálvastagságú, egyenes struktúra, tömör hatás"
            number="KZ"
            number2="02"
            text="Vastagabb, sűrűbb szálstruktúra erős természetes csillogással. Ha vastag, sűrű hajad van, vagy ha látványos teltséget szeretnél — ez a tiéd."
            image="/brand/kz-origin.jpg"
            link="/termekek?o=kazah"
            testid="origin-kz"
            reversed
          />
        </div>
      </div>
    </section>
  );
}

function OriginCard({ title, subtitle, number, number2, text, image, link, testid, reversed }) {
  return (
    <Link
      to={link}
      data-testid={testid}
      className={`group block reveal ${reversed ? "delay-2 md:translate-y-12" : "delay-1"}`}
    >
      <div className="relative">
        <div className="he-img-wrap aspect-[4/5]">
          <img src={image} alt={title} className="he-img w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

          {/* Big number top-left */}
          <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
            <span className="figure italic text-[color:var(--he-gold)] text-[3.6rem] md:text-[5.2rem] leading-[0.85]">
              {number}
            </span>
            <span className="overline-xs text-white/85">№ {number2}</span>
          </div>

          {/* Bottom title */}
          <div className="absolute bottom-7 left-7 right-7">
            <p className="overline-xs text-white/80 mb-3">{subtitle}</p>
            <h3 className="font-serif-display text-white text-4xl md:text-6xl leading-none">
              {title}
            </h3>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-12 gap-6 items-start">
        <p className="col-span-9 text-[color:var(--he-ink-soft)] font-light leading-relaxed text-[15px] max-w-md">
          {text}
        </p>
        <div className="col-span-3 flex justify-end pt-2">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[color:var(--he-line)] group-hover:border-[color:var(--he-gold)] group-hover:bg-[color:var(--he-gold)] transition-all duration-500">
            <ArrowUpRight size={16} className="group-hover:text-white transition" />
          </span>
        </div>
      </div>
    </Link>
  );
}
